import { useEffect, useRef, useState } from "react";

// Wat de server op het kanaal van een project zet (ca_project/project/events.py).
// Een seintje, geen inhoud: het zegt welk soort ding veranderde, niet wat er
// nu staat — dat haalt de client zelf op.
export interface WorkspaceEvent {
  workspace: number;
  kind: "research" | "document" | "report";
  id: number;
  status?: string;
}

// Een eigen soort, alleen voor de client: de verbinding was weg en is er weer.
// Wat er tijdens de onderbreking gebeurde is nooit als seintje binnengekomen,
// dus de aanroeper laadt dan opnieuw.
export type WorkspaceSignal = WorkspaceEvent | { kind: "reconnected" };

export type ConnectionState = "connecting" | "open" | "broken";

export interface EventChannel {
  onopen: ((event: unknown) => void) | null;
  onmessage: ((event: { data: string }) => void) | null;
  onerror: ((event: unknown) => void) | null;
  close(): void;
}

const openEventSource = (url: string): EventChannel =>
  new EventSource(url) as unknown as EventChannel;

/**
 * Luister naar de gebeurtenissen van één project.
 *
 * Geen hook maar een gewone functie, zodat de mechaniek los van React te
 * beproeven is; `useWorkspaceEvents` hieronder is het dunne laagje eromheen.
 * `EventSource` stuurt de sessiecookie mee en verbindt na een storing zelf
 * opnieuw — wij hoeven alleen te merken *dát* het even weg was.
 *
 * Geeft terug hoe je weer afsluit.
 */
export function subscribeToWorkspaceEvents(
  workspaceId: number,
  onSignal: (signal: WorkspaceSignal) => void,
  onState: (state: ConnectionState) => void = () => {},
  open: (url: string) => EventChannel = openEventSource
): () => void {
  const channel = open(`/api/workspaces/${workspaceId}/events`);
  let wasBroken = false;

  channel.onopen = () => {
    onState("open");
    if (wasBroken) {
      wasBroken = false;
      onSignal({ kind: "reconnected" });
    }
  };
  channel.onmessage = (event) => {
    try {
      onSignal(JSON.parse(event.data) as WorkspaceEvent);
    } catch {
      // Een onleesbaar seintje is geen reden om de verbinding op te geven.
    }
  };
  channel.onerror = () => {
    // EventSource probeert het zelf opnieuw; we onthouden alleen dat er een
    // gat was, zodat `onopen` de achterstand kan laten inhalen.
    wasBroken = true;
    onState("broken");
  };

  return () => channel.close();
}

export function useWorkspaceEvents(
  workspaceId: number | undefined,
  onSignal: (signal: WorkspaceSignal) => void
): ConnectionState {
  const handler = useRef(onSignal);
  // In een effect en niet tijdens de render: een ref bijwerken hoort niet in
  // de renderfase thuis (react-hooks/refs). Zo mag de handler bij elke render
  // veranderen zonder de verbinding opnieuw op te zetten.
  useEffect(() => {
    handler.current = onSignal;
  });
  const [state, setState] = useState<ConnectionState>("connecting");

  useEffect(() => {
    if (!workspaceId) return;
    return subscribeToWorkspaceEvents(workspaceId, (signal) => handler.current(signal), setState);
  }, [workspaceId]);

  return state;
}
